import React, { useMemo } from 'react';
import { Joyride } from 'react-joyride';
import { useMediaQuery, useTheme } from '@mui/material';
import useTutorial from 'contexts/Tutorial';
import {
  getJoyrideOptions,
  JOYRIDE_MOBILE_FLOATING_OPTIONS,
  shouldCompleteTutorialFromJoyrideEvent,
} from 'utils/joyrideOnEvent';

const DashboardTour = () => {
  const { shouldShowTutorial, completeTutorial, activeTutorialId } = useTutorial();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const tutorialId = 'dashboard';

  const steps = useMemo(() => {
    const baseSteps = [
      {
        target: 'body',
        content: 'Welcome to your dashboard! Here’s a quick tour of the main sections.',
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-tour="dashboard-leaderboard"]',
        content: 'See club members and their consistency with Habit goals.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="dashboard-member-profile"]',
        content: 'Tap on a member\'s profile picture to see their goal progress and activity.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="dashboard-meeting"]',
        content: 'Keep track of upcoming meetings and what’s on deck.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="dashboard-goals"]',
        content: 'Quickly track and update your personal goals for today.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="dashboard-club-goals"]',
        content:
          'Club goals show everyone’s progress toward shared targets. Swipe between goals and tap a card to log your contribution.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="dashboard-books-title"]',
        content: 'Review upcoming books and update your reading progress.',
        // On mobile this target sits near the bottom nav; keep the tooltip above it.
        placement: isMobile ? 'top' : 'bottom',
        disableBeacon: true,
      },
      {
        target: '[data-tour="dashboard-feed"]',
        content: 'Catch recent club activity and updates in the feed preview.',
        placement: isMobile ? 'top' : 'bottom',
        disableBeacon: true,
      },
    ];

    if (isMobile) {
      baseSteps.push({
        target: '[data-tour="nav-container"]',
        content: 'Use the bottom navigation to move between Home, Feed, Goals, and Books.',
        placement: 'top',
        disableBeacon: true,
      });
    }

    return baseSteps;
  }, [isMobile]);

  const run = (
    (shouldShowTutorial(tutorialId) || activeTutorialId === tutorialId)
    && activeTutorialId !== 'goalPauseRelease'
  );

  const handleEvent = (data) => {
    if (shouldCompleteTutorialFromJoyrideEvent(data)) {
      completeTutorial(tutorialId);
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      scrollToFirstStep
      options={getJoyrideOptions({ isMobile })}
      floatingOptions={isMobile ? JOYRIDE_MOBILE_FLOATING_OPTIONS : undefined}
      onEvent={handleEvent}
    />
  );
};

export default DashboardTour;
